import * as React from "react";
import { VideoContext } from "../../context/video_context";
import useAuth from "../../hooks/auth_hook";
import moment from "moment";

export type Video = {
  imgLink: string;
  title: string;
  views: string;
  uploaded: Date;
  video_id: string;
  handlePageChangeUp?: any;
};
const calculateDaysHoursYearsAgo = (date: Date) => {
  const now = new Date();
  const then = new Date(date);
  // console.log({ now, date })
  const diff = now.getTime() - then.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));
  if (years > 0) {
    return `${years} years ago`;
  }
  if (days > 0) {
    return `${days} days ago`;
  }
  if (hours > 0) {
    return `${hours} hours ago`;
  }
  if (minutes > 0) {
    return `${minutes} minutes ago`;
  }
  return `${seconds} seconds ago`;
};
export default function VideoCard(fn: Video) {
  const videoContext = React.useContext(VideoContext);
  const setSelectedVideo = videoContext?.setSelectedVideo || (() => {
  });
  const { user, isAuthenticated } = useAuth();

  return (
    <div
      className="bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer h-full flex flex-col"
      onClick={() => {
        setSelectedVideo(fn.video_id);
        fn.handlePageChangeUp();
      }}
    >
      <div className="relative w-full overflow-hidden bg-black">
        <img
          className="w-full h-auto object-cover"
          src={fn.imgLink}
          alt={`Thumbnail for Youtube Video titled: ${fn.title}`}
        />
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-start gap-2 mb-2">
          {user?.picture && (
            <img
              src={user.picture}
              alt="User avatar"
              className="h-8 w-8 rounded-full shrink-0"
            />
          )}
          <h6 className="text-sm font-medium line-clamp-2 flex-1">{fn.title}</h6>
        </div>
        <div className="text-xs text-muted-foreground mt-auto">
          <span>{fn.views} views</span> • <span>{calculateDaysHoursYearsAgo(fn.uploaded)}</span>
        </div>
      </div>
    </div>
  );
}

export type Test = {
  imgLink: string;
  title: string;
  video_id: string;
  filename: string;
  duration: number;
  order: number;
  handlePageChangeUp?: any;
  loadFromImgLink?: boolean;
  active?: boolean;
  activated?: string;
  deactivated?: string;
  tested?: boolean;
  time_to_test?: string;
};

export function TestCard(fn: Test) {
  const videoContext = React.useContext(VideoContext);
  const setSelectedVideo = videoContext?.setSelectedVideo || (() => {
  });
  const { user, isAuthenticated } = useAuth();
  
  const getTestTime = () => {
    return (fn?.active && fn?.activated) ? new Date(fn?.activated) : fn?.time_to_test ? new Date(fn?.time_to_test) : new Date(new Date().getTime() + fn.order * fn.duration * 1000);
  };
  const getDateStart = () => {
    const now = getTestTime();
    const then = new Date(new Date().getTime() + fn.order * fn.duration * 1000);
    return moment(now).format("ddd, h:mm a");
  };
  const getDateEnd = () => {
    const now = getTestTime();
    const then = new Date(now.getTime() + (fn.order + 1) * fn.duration * 1000);
    return moment(then).format("ddd, h:mm a");
  };
  
  return (
    <div
      className="bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer h-full flex flex-col"
      onClick={() => {
        setSelectedVideo(fn.video_id);
        fn.handlePageChangeUp();
      }}
    >
      <div className="relative w-full overflow-hidden bg-black">
        <img
          src={fn.imgLink}
          className="w-full object-contain max-h-[200px]"
          alt={`Thumbnail for Youtube Video titled: ${fn.title}`}
        />
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-start gap-2 mb-2">
          {user?.picture && (
            <img
              src={user.picture}
              alt="User avatar"
              className="h-8 w-8 rounded-full shrink-0"
            />
          )}
          <h6 className="text-sm font-medium line-clamp-2 flex-1">{fn.title}</h6>
        </div>
        <div className="text-xs text-muted-foreground mt-auto">
          <span>{getDateStart()}</span> - <span>{getDateEnd()}</span>
        </div>
      </div>
    </div>
  );
}

export function LargeVideoCard(fn: Video | Test) {
  return (
    <div className="py-2.5 px-2.5 flex flex-col">
      <div className="min-w-[300px] max-h-[340px] bg-card border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden cursor-pointer flex flex-col">
        <div className="relative w-full overflow-hidden bg-black">
          <img
            className="w-full h-auto object-cover"
            src={"filename" in fn ? fn.imgLink : process.env.PUBLIC_URL + fn.imgLink}
            alt={`Thumbnail for Youtube Video titled: ${fn.title}`}
          />
        </div>
        <div className="p-3 flex-1 flex flex-col">
          <h6 className="text-base font-medium mb-2">{fn.title}</h6>
          <div className="flex items-center text-xs text-muted-foreground mt-auto">
            <span className="whitespace-nowrap">
              {"views" in fn ? `${fn.views} views` : "Test Content"}
            </span>
            <span className="mx-1">•</span>
            <span className="whitespace-nowrap">
              {"duration" in fn ?
                (fn.duration < 3600 ? `${fn.duration / 60} minutes` :
                  fn.duration < 86400 ? `${fn.duration / 3600} hours` :
                    fn.duration < 604800 ? `${fn.duration / 86400} days` :
                      `${fn.duration / 604800} weeks`)
                : `${fn.uploaded}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
